import { createNavigation } from 'next-intl/navigation'
import { useCallback } from 'react'
import type { ComponentProps } from 'react'
import type { ParsedUrlQuery } from 'node:querystring'
import { hasSameDomain } from './shared/utils/routesUtils'
import { routing } from './routing'

export { locales, routing } from './routing'

const { Link: NextIntlLink, redirect, usePathname, useRouter: useNextIntlRouter } = createNavigation(routing)

type RouterPushParameters = Parameters<ReturnType<typeof useNextIntlRouter>['push']>
type LinkProps = ComponentProps<typeof NextIntlLink>

interface UrlObject {
    pathname: string
    query?: ParsedUrlQuery
}

/**
 * Enhanced Link component that preserves company_id
 */
export const Link = ({ href, ...rest }: LinkProps) => {
    /**
     * Enhanced href that includes company_id query parameter if present
     */
    const getEnhancedHref = () => {
        if (typeof window === 'undefined') {
            return href
        }

        const currentUrl = new URL(window.location.href)
        const companyId = currentUrl.searchParams.get('company_id')

        if (!companyId || !hasSameDomain(href)) {
            return href
        }

        if (typeof href === 'string') {
            const newUrl = new URL(href, window.location.origin)
            newUrl.searchParams.set('company_id', companyId)
            return newUrl.pathname + newUrl.search
        }

        if (typeof href === 'object' && href !== null) {
            // eslint-disable-next-line typescript/no-unsafe-type-assertion -- the value has this shape at runtime, as the code has always read it
            const urlObject = href as UrlObject
            return {
                pathname: urlObject.pathname,
                query: {
                    ...urlObject.query,
                    company_id: companyId
                }
            }
        }

        return href
    }

    return <NextIntlLink href={getEnhancedHref()} {...rest} />
}

/**
 * Custom router hook that enhances the next-intl router with automatic scroll-to-top behavior
 * This is needed because the default router.push() doesn't handle scrolling in custom scroll containers
 * that use overflow-y-auto (like our RootLayout component)
 */
export const useRouter = () => {
    const router = useNextIntlRouter()

    /**
     * Enhanced push method that includes scroll-to-top behavior
     * Preserves all original push functionality while adding automatic scrolling
     * Preserves company_id query parameter if present
     */
    const enhancedPush = useCallback(
        (...args: RouterPushParameters) => {
            const [path] = args

            const currentUrl = new URL(window.location.href)
            const companyId = currentUrl.searchParams.get('company_id')

            if (companyId) {
                if (typeof path === 'string') {
                    const newUrl = new URL(path, window.location.origin)
                    if (!newUrl.searchParams.has('company_id')) {
                        newUrl.searchParams.set('company_id', companyId)
                    }
                    args[0] = newUrl.pathname + newUrl.search
                } else if (typeof path === 'object' && path !== null) {
                    const urlObject = path
                    if (!urlObject.query?.company_id) {
                        args[0] = {
                            pathname: urlObject.pathname || '/',
                            query: {
                                ...urlObject.query,
                                company_id: companyId
                            }
                        }
                    }
                }
            }

            const result = router.push(...args)

            const mainContainer = document.querySelector('main')
            if (mainContainer) {
                mainContainer.scrollTop = 0
            }

            return result
        },
        [router]
    )

    return {
        ...router,
        push: enhancedPush
    }
}

export { redirect, usePathname }
